<template>
  <b-modal
    v-model="showModal"
    :title="isEditMode ? 'Edit MR' : 'Add MR'"
    size="lg"
    no-close-on-backdrop
    @hidden="$emit('modal-closed')"
    @ok="submitHandler"
  >
    <b-form
      @submit.prevent="submitHandler"
    >
      <b-row>
        <b-col cols="12">
          <b-alert
            variant="danger"
            :show="errorMessage ? true : false"
          >
            <div
              v-if="typeof errorMessage === 'string'"
              class="alert-body"
            >
              <p>
                {{ errorMessage }}
              </p>
            </div>
            <div
              v-else-if="errorMessage.detail"
              class="alert-body"
            >
              <p>
                {{ errorMessage.detail }}
              </p>
            </div>
            <div
              v-else-if="errorMessage.non_field_errors"
              class="alert-body"
            >
              <p>
                {{ errorMessage.non_field_errors[0] }}
              </p>
            </div>
            <div
              v-else-if="errorMessage.message"
              class="alert-body"
            >
              <p>
                {{ errorMessage.message }}
              </p>
            </div>
          </b-alert>
        </b-col>

        <!-- Basic MR Information -->
        <b-col cols="6">
          <b-form-group
            label="MR Name"
            label-for="mrName"
            :state="mrNameValidationState"
          >
            <b-form-input
              id="mrName"
              v-model="mrName"
              type="text"
              placeholder="Enter MR Name (max 15 characters)"
              maxlength="15"
              :state="mrNameValidationState"
              @input="onMrNameInput"
            />
            <b-form-invalid-feedback>
              {{ mrNameErrorMessage }}
            </b-form-invalid-feedback>
            <p
              v-if="errorMessage && errorMessage.name"
              class="error-message"
            >
              {{ errorMessage.name[0] }}
            </p>
          </b-form-group>
        </b-col>

        <b-col cols="6">
          <b-form-group
            label="Company"
            label-for="company"
          >
            <custom-v-select
              id="company"
              v-model="selectedCompany"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="companyOptions"
              label="text"
              :class="{ 'is-invalid': companyValidationState === false }"
            />
            <div
              v-if="companyErrorMessage"
              class="error-message small"
            >
              {{ companyErrorMessage }}
            </div>
            <p
              v-if="errorMessage && errorMessage.company"
              class="error-message"
            >
              {{ errorMessage.company[0] }}
            </p>
          </b-form-group>
        </b-col>

        <b-col cols="6">
          <b-form-group
            label="Post"
            label-for="post"
          >
            <custom-v-select
              id="post"
              v-model="selectedPost"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="postOptions"
              label="text"
              :class="{ 'is-invalid': postValidationState === false }"
            />
            <div
              v-if="postErrorMessage"
              class="error-message small"
            >
              {{ postErrorMessage }}
            </div>
            <p
              v-if="errorMessage && errorMessage.post"
              class="error-message"
            >
              {{ errorMessage.post[0] }}
            </p>
          </b-form-group>
        </b-col>

        <b-col cols="6">
          <b-form-group>
            <label>Status</label>
            <div>
              <b-form-checkbox
                v-model="isActive"
                switch
                size="lg"
              >
                {{ isActive ? 'Active' : 'Inactive' }}
              </b-form-checkbox>
            </div>
          </b-form-group>
        </b-col>

        <!-- Mobile Numbers Section -->
        <b-col cols="12">
          <h6>Mobile Numbers</h6>
        </b-col>

        <b-col cols="12">
          <div
            v-for="(mobile, index) in mrMobiles"
            :key="`mobile-${index}`"
            class="mb-1"
          >
            <b-row align-v="center">
              <b-col cols="1">
                <div class="text-center font-weight-bold">
                  {{ index + 1 }}.
                </div>
              </b-col>
              <b-col cols="4">
                <b-form-input
                  :id="`mobile-${index}`"
                  v-model="mobile.mobile"
                  type="text"
                  placeholder="Enter 10 digit mobile number"
                  maxlength="10"
                  :state="getMobileValidationState(index)"
                  @input="onMobileInput(index, $event)"
                />
                <div
                  v-if="getMobileErrorMessage(index)"
                  class="error-message small"
                >
                  {{ getMobileErrorMessage(index) }}
                </div>
              </b-col>
              <b-col cols="2">
                <b-form-radio
                  v-model="whatsappSelectedIndex"
                  :name="'whatsapp-radio'"
                  :value="index"
                  @change="onWhatsappChange(index)"
                >
                  <i class="fab fa-whatsapp text-success" /> WhatsApp
                </b-form-radio>
              </b-col>
              <b-col cols="2">
                <b-form-radio
                  v-model="primaryMobileIndex"
                  :name="'primary-mobile-radio'"
                  :value="index"
                  @change="onPrimaryMobileChange(index)"
                >
                  Primary
                </b-form-radio>
              </b-col>
              <b-col cols="2">
                <feather-icon
                  v-if="mrMobiles.length > 1"
                  icon="TrashIcon"
                  class="cursor-pointer"
                  color="red"
                  @click="removeMobile(index)"
                />
              </b-col>
              <b-col cols="1">
                <!-- Empty column for spacing -->
              </b-col>
            </b-row>
          </div>

          <b-button
            variant="outline-primary"
            size="sm"
            @click="addMobile"
          >
            <i class="fas fa-plus" /> Add Mobile Number
          </b-button>
        </b-col>

        <!-- Email Addresses Section -->
        <b-col
          cols="12"
          class="mt-2"
        >
          <h6>Email Addresses</h6>
        </b-col>

        <b-col cols="12">
          <div
            v-for="(email, index) in mrEmails"
            :key="`email-${index}`"
            class="mb-1"
          >
            <b-row align-v="center">
              <b-col cols="1">
                <div class="text-center font-weight-bold">
                  {{ index + 1 }}.
                </div>
              </b-col>
              <b-col cols="6">
                <b-form-input
                  :id="`email-${index}`"
                  v-model="email.email"
                  type="email"
                  placeholder="Enter email address"
                  :state="getEmailValidationState(index)"
                />
                <div
                  v-if="getEmailErrorMessage(index)"
                  class="error-message small"
                >
                  {{ getEmailErrorMessage(index) }}
                </div>
              </b-col>
              <b-col cols="3">
                <b-form-radio
                  v-model="primaryEmailIndex"
                  :name="'primary-email-radio'"
                  :value="index"
                  @change="onPrimaryEmailChange(index)"
                >
                  Primary
                </b-form-radio>
              </b-col>
              <b-col cols="2">
                <feather-icon
                  v-if="mrEmails.length > 1"
                  icon="TrashIcon"
                  class="cursor-pointer"
                  color="red"
                  @click="removeEmail(index)"
                />
              </b-col>
            </b-row>
          </div>

          <b-button
            variant="outline-primary"
            size="sm"
            @click="addEmail"
          >
            <i class="fas fa-plus" /> Add Email Address
          </b-button>
        </b-col>

      </b-row>
    </b-form>

    <template #modal-footer="{ ok, cancel }">
      <b-button
        variant="secondary"
        @click="cancel()"
      >
        Cancel
      </b-button>
      <b-button
        variant="primary"
        :disabled="saving || loading"
        @click="ok()"
      >
        {{ isEditMode ? 'Update MR' : 'Create MR' }}
        <b-spinner
          v-if="saving || loading"
          small
          label="Small Spinner"
        />
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import {
  BModal, BRow, BCol, BFormGroup, BButton, BFormInput, BForm, BSpinner, BAlert, BFormRadio, BFormInvalidFeedback, BFormCheckbox,
} from 'bootstrap-vue'
import CustomVSelect from '@/components/UI/CustomVSelect.vue'
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  components: {
    CustomVSelect,
    BModal,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BForm,
    BButton,
    BSpinner,
    BAlert,
    BFormRadio,
    BFormInvalidFeedback,
    BFormCheckbox,
  },

  props: {
    id: {
      type: [String, Number],
      default: null,
    },
  },

  data() {
    return {
      showModal: true,
      loading: false,
      mrName: '',
      selectedCompany: null,
      companyOptions: [],
      selectedPost: null,
      postOptions: [
        { value: 'Medical Representative', text: 'Medical Representative' },
        { value: 'Area Manager', text: 'Area Manager' },
        { value: 'Regional Manager', text: 'Regional Manager' },
        { value: 'Zonal Manager', text: 'Zonal Manager' },
        { value: 'Business Head', text: 'Business Head' },
      ],
      isActive: true,
      mrMobiles: [
        {
          mobile: '',
          is_whatsapp: false,
          is_primary: true,
        },
      ],
      mrEmails: [
        {
          email: '',
          is_primary: true,
        },
      ],
      whatsappSelectedIndex: null,
      primaryMobileIndex: 0,
      primaryEmailIndex: 0,
      saving: false,
      errorMessage: {},
      showValidationErrors: false,
    }
  },

  computed: {
    isEditMode() {
      return !!(this.id)
    },
    mrNameValidationState() {
      if (this.showValidationErrors) {
        if (!this.mrName || this.mrName.trim().length === 0 || this.mrName.trim().length > 15) {
          return false
        }
        return null // Don't show green border for valid state
      }
      return null
    },
    mrNameErrorMessage() {
      if (this.showValidationErrors) {
        if (!this.mrName || this.mrName.trim().length === 0) {
          return 'MR Name is required'
        }
        if (this.mrName.trim().length > 15) {
          return 'MR Name cannot exceed 15 characters'
        }
      }
      return ''
    },
    companyValidationState() {
      if (this.showValidationErrors) {
        return !!(this.selectedCompany)
      }
      return null
    },
    companyErrorMessage() {
      if (this.showValidationErrors && !this.selectedCompany) {
        return 'Company is required'
      }
      return ''
    },
    postValidationState() {
      if (this.showValidationErrors) {
        return !!(this.selectedPost)
      }
      return null
    },
    postErrorMessage() {
      if (this.showValidationErrors && !this.selectedPost) {
        return 'Post is required'
      }
      return ''
    },
  },

  created() {
    this.fetchOptions()
    if (this.isEditMode) {
      this.fetchMrDetails()
    } else {
      const [firstPost] = this.postOptions // Default to first option for add mode
      this.selectedPost = firstPost
    }
  },

  methods: {
    fetchOptions() {
      // Fetch company options
      axios.get('/company_options')
        .then(res => {
          this.companyOptions = res.data.data
        })
        .catch(error => {
          this.errorMessage = error?.response?.data || 'Failed to fetch Company Options'
        })
    },

    fetchMrDetails() {
      this.loading = true
      axios.get(`/mr/${this.id}/`)
        .then(res => {
          const { data } = res

          // Bind basic information
          this.mrName = data.name || ''
          this.isActive = data.is_active !== undefined ? data.is_active : true

          // Find and set company
          if (data.company && this.companyOptions.length > 0) {
            this.selectedCompany = this.companyOptions.find(company => company.id === data.company) || null
          }

          // Find and set post
          this.selectedPost = this.postOptions.find(post => post.value === data.post) || this.postOptions[0]

          // Bind mobile numbers
          if (data.mr_mobiles && data.mr_mobiles.length > 0) {
            this.mrMobiles = data.mr_mobiles.map(mobile => ({
              mobile: mobile.mobile || '',
              is_whatsapp: mobile.is_whatsapp || false,
              is_primary: mobile.is_primary || false,
            }))

            // Set primary mobile index
            const primaryMobileIdx = this.mrMobiles.findIndex(mobile => mobile.is_primary)
            this.primaryMobileIndex = primaryMobileIdx !== -1 ? primaryMobileIdx : 0

            // Set whatsapp index
            const whatsappIdx = this.mrMobiles.findIndex(mobile => mobile.is_whatsapp)
            this.whatsappSelectedIndex = whatsappIdx !== -1 ? whatsappIdx : null
          }

          // Bind email addresses
          if (data.mr_emails && data.mr_emails.length > 0) {
            this.mrEmails = data.mr_emails.map(email => ({
              email: email.email || '',
              is_primary: email.is_primary || false,
            }))

            // Set primary email index
            const primaryEmailIdx = this.mrEmails.findIndex(email => email.is_primary)
            this.primaryEmailIndex = primaryEmailIdx !== -1 ? primaryEmailIdx : 0
          }

          this.loading = false
        })
        .catch(error => {
          this.errorMessage = error?.response?.data || 'Failed to fetch MR details'
          this.loading = false
        })
    },

    onMrNameInput(value) {
      // Automatically convert to uppercase and limit to 15 characters
      this.mrName = value.toUpperCase().slice(0, 15)
    },

    onMobileInput(index, value) {
      // Only allow numeric input and limit to exactly 10 digits
      const numericValue = value.replace(/\D/g, '')
      this.mrMobiles[index].mobile = numericValue.slice(0, 10)
    },

    getMobileValidationState(index) {
      if (this.showValidationErrors) {
        const { mobile } = this.mrMobiles[index]
        if (!mobile || mobile.length !== 10 || !/^\d{10}$/.test(mobile)) {
          return false
        }
        return null // Don't show green border for valid state
      }
      return null
    },

    getMobileErrorMessage(index) {
      if (this.showValidationErrors) {
        const { mobile } = this.mrMobiles[index]
        if (!mobile || mobile.length === 0) {
          return 'Mobile number is required'
        }
        if (mobile.length !== 10 || !/^\d{10}$/.test(mobile)) {
          return 'Mobile number must be exactly 10 digits'
        }
      }
      return ''
    },

    getEmailValidationState(index) {
      if (this.showValidationErrors) {
        const { email } = this.mrEmails[index]
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!email || !emailRegex.test(email)) {
          return false
        }
        return null // Don't show green border for valid state
      }
      return null
    },

    getEmailErrorMessage(index) {
      if (this.showValidationErrors) {
        const { email } = this.mrEmails[index]
        if (!email || email.length === 0) {
          return 'Email address is required'
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!emailRegex.test(email)) {
          return 'Please enter a valid email address'
        }
      }
      return ''
    },

    onWhatsappChange(index) {
      // Reset all whatsapp flags
      this.mrMobiles.forEach((mobile, i) => {
        this.mrMobiles[i].is_whatsapp = (i === index)
      })
    },

    onPrimaryMobileChange(index) {
      // Update primary mobile index
      this.primaryMobileIndex = index
      // Update all mobile primary flags
      this.mrMobiles.forEach((mobile, i) => {
        this.mrMobiles[i].is_primary = (i === index)
      })
    },

    onPrimaryEmailChange(index) {
      // Update primary email index
      this.primaryEmailIndex = index
      // Update all email primary flags
      this.mrEmails.forEach((email, i) => {
        this.mrEmails[i].is_primary = (i === index)
      })
    },

    addMobile() {
      this.mrMobiles.push({
        mobile: '',
        is_whatsapp: false,
        is_primary: false,
      })
    },

    removeMobile(index) {
      if (this.mrMobiles.length > 1) {
        // If removing the primary mobile, set first one as primary
        if (this.primaryMobileIndex === index) {
          this.primaryMobileIndex = 0
        } else if (this.primaryMobileIndex > index) {
          this.primaryMobileIndex -= 1
        }

        // If removing the whatsapp mobile, reset whatsapp selection
        if (this.whatsappSelectedIndex === index) {
          this.whatsappSelectedIndex = null
        } else if (this.whatsappSelectedIndex > index) {
          this.whatsappSelectedIndex -= 1
        }

        this.mrMobiles.splice(index, 1)

        // Update primary flags
        this.mrMobiles.forEach((mobile, i) => {
          this.mrMobiles[i].is_primary = (i === this.primaryMobileIndex)
        })

        // Update whatsapp flags
        this.mrMobiles.forEach((mobile, i) => {
          this.mrMobiles[i].is_whatsapp = (i === this.whatsappSelectedIndex)
        })
      }
    },

    addEmail() {
      this.mrEmails.push({
        email: '',
        is_primary: false,
      })
    },

    removeEmail(index) {
      if (this.mrEmails.length > 1) {
        // If removing the primary email, set first one as primary
        if (this.primaryEmailIndex === index) {
          this.primaryEmailIndex = 0
        } else if (this.primaryEmailIndex > index) {
          this.primaryEmailIndex -= 1
        }

        this.mrEmails.splice(index, 1)

        // Update primary flags
        this.mrEmails.forEach((email, i) => {
          this.mrEmails[i].is_primary = (i === this.primaryEmailIndex)
        })
      }
    },

    validateForm() {
      let isValid = true

      // Validate MR Name
      if (!this.mrName || this.mrName.trim().length === 0 || this.mrName.trim().length > 15) {
        isValid = false
      }
      // Validate Company
      if (!this.selectedCompany) {
        isValid = false
      }
      // Validate Post
      if (!this.selectedPost) {
        isValid = false
      }
      // Validate at least one mobile
      if (this.mrMobiles.length === 0) {
        isValid = false
      }
      // Validate mobile numbers
      const invalidMobiles = this.mrMobiles.filter(mobile => !mobile.mobile || mobile.mobile.length !== 10 || !/^\d{10}$/.test(mobile.mobile))
      if (invalidMobiles.length > 0) {
        isValid = false
      }
      // Validate at least one email
      if (this.mrEmails.length === 0) {
        isValid = false
      }
      // Validate email addresses
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      const invalidEmails = this.mrEmails.filter(email => !email.email || !emailRegex.test(email.email))
      if (invalidEmails.length > 0) {
        isValid = false
      }
      // Ensure at least one primary mobile and email
      const hasPrimaryMobile = this.mrMobiles.some(mobile => mobile.is_primary)
      const hasPrimaryEmail = this.mrEmails.some(email => email.is_primary)
      if (!hasPrimaryMobile || !hasPrimaryEmail) {
        isValid = false
      }
      return isValid
    },

    submitHandler(event) {
      event.preventDefault()
      this.showValidationErrors = true
      this.errorMessage = {} // Clear any previous server errors
      if (!this.validateForm()) {
        this.saving = false
        return
      }
      const payload = {
        name: this.mrName,
        company: this.selectedCompany.id,
        company_name: this.selectedCompany.text,
        post: this.selectedPost.value,
        is_active: this.isActive,
        mr_mobiles: this.mrMobiles.filter(mobile => mobile.mobile),
        mr_emails: this.mrEmails.filter(email => email.email),
      }

      this.saving = true

      const request = this.isEditMode
        ? axios.patch(`/mr/${this.id}/`, payload)
        : axios.post('/mr/', payload)

      request
        .then(res => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: res.data.message || (this.isEditMode ? 'MR Updated Successfully' : 'MR Created Successfully'),
              icon: 'CheckIcon',
              variant: 'success',
            },
          })

          this.$emit(this.isEditMode ? 'mr-updated' : 'mr-added')
          this.saving = false
        }).catch(error => {
          this.errorMessage = error?.response?.data?.error || error?.response?.data || 'Something went wrong'
          this.saving = false
        })
    },
  },
}
</script>

<style scoped>
.error-message {
  color: red;
}

.border {
  border: 1px solid #dee2e6 !important;
}

/* Custom styling for invalid custom-v-select */
.is-invalid {
  border-color: #dc3545 !important;
}

/* Remove green validation styling */
.form-control.is-valid,
.was-validated .form-control:valid {
  border-color: #ced4da !important;
  background-image: none !important;
}

.form-control.is-valid:focus,
.was-validated .form-control:valid:focus {
  border-color: #80bdff !important;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25) !important;
}
</style>
